import React, { memo, useState } from 'react';

import { createPortal } from 'react-dom';

import { Box, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { RICHARD_MARK_SIZES } from '~/components/brand';

import AnswerLensUndo from '~/pages/LeLens/AnswerLensUndo';
import { ProposalCollapse } from '~/pages/LeLens/proposalCollapse';

import type { LensIntent } from '~/types/leData';
import type { Lens } from '~/types/leLens';

import { hasLeDataProvenanceDisplayData } from '~/utils/leData/provenance';

import AnswerCard from '../chat/AnswerCard';
import AnswerSkeleton from '../chat/AnswerSkeleton';
import LeDataMessageBlocks, { messageHasChart, messagePrefersTable } from '../chat/LeDataMessageBlocks';
import RichardMonogram from '../chat/RichardMonogram';
import type { DashboardDestinations } from '../chat/dashboardDestinations';
import { hasSelfFramedBlock } from '../chat/selfFramedBlocks';
import type { AnswerSpeech } from '../chat/useAnswerSpeech';

import AnswerActions from './AnswerActions';
import AnswerReceipt, { AnswerMethod, hasAnswerMethod, hasAnswerReceipt } from './AnswerReceipt';
import LiveWork from './LiveWork';
import SteerNotes from './SteerNotes';
import TurnFaultAlert, { type TurnFault } from './TurnFaultAlert';
import { type TurnStatus, useWorkSummary } from './WorkSummary';
import { type AssistantTurn, answerMessageOf, businessWorkRows, withoutTableOnlyProse } from './projectTurn';
import { useHeldHeight } from './useHeldHeight';

type LensScopedBlockProps = Pick<
  React.ComponentProps<typeof LeDataMessageBlocks>,
  'lensSaveTarget' | 'forcedReplacementCard' | 'cardApply' | 'allowGlobalLensDrafts'
>;

export interface LensHandlers {
  latestLensScopeBlockId: string | null;
  latestLensCreatedBlockId?: string | null;
  lensTurnSending: boolean;
  onSendLensTurn: (question: string, intent: LensIntent) => void;
  onLensCreated?: (lensId: string) => void;
}

interface AssistantTurnViewProps extends LensScopedBlockProps {
  turn: AssistantTurn;
  status: TurnStatus;
  statusText: string;
  pendingSteers: ReadonlyArray<string>;
  sourceQuestion?: string;
  replyText?: string;
  replyQuestionAnswer?: Extract<LensIntent, { kind: 'answer' }>['questionAnswer'];
  fault: TurnFault | null;
  debug: boolean;
  elapsedMs?: number;
  lens: LensHandlers;
  prominentLensSave: boolean;
  showAnswerActions?: boolean;
  compactSkeleton?: boolean;
  speech?: AnswerSpeech;
  dashboardDestinations?: DashboardDestinations;
  onSavedLens?: (lens: Lens) => void;
  onBuildDashboard?: (
    brief: string,
    context?: { hasExecutedResults: boolean; newLens?: boolean; sourceQuestion?: string },
  ) => void;
}

export default memo(function AssistantTurnView({
  turn,
  status,
  statusText,
  pendingSteers,
  sourceQuestion,
  replyText,
  replyQuestionAnswer,
  fault,
  debug,
  elapsedMs,
  lens,
  prominentLensSave,
  showAnswerActions = true,
  compactSkeleton = false,
  speech,
  dashboardDestinations,
  onSavedLens,
  onBuildDashboard,
  lensSaveTarget,
  allowGlobalLensDrafts,
  forcedReplacementCard,
  cardApply,
}: AssistantTurnViewProps) {
  const theme = useTheme();
  const live = status === 'live';
  const settledCleanly = status === 'complete';
  const answerArea = useHeldHeight(live);
  const provenance = turn.provenance && hasLeDataProvenanceDisplayData(turn.provenance) ? turn.provenance : undefined;
  const chartLed = messageHasChart(turn.blocks) && !messagePrefersTable(turn.blocks);
  const lensConversationTurn = turn.blocks.some((block) => block.type === 'lens_scope');
  const awaitingLensAnswer =
    !replyText && turn.blocks.some((block) => block.type === 'lens_scope' && Boolean(block.payload.question));
  const [draftCollapsed, setDraftCollapsed] = useState(false);
  const selfFramed = hasSelfFramedBlock(turn.blocks) && !draftCollapsed;
  const framed = !selfFramed && (settledCleanly || turn.hasAnswerText);
  const activityRows = businessWorkRows(turn.timeline);
  const hasAnswerBody =
    turn.hasAnswerText || turn.blocks.some((block) => !['lens_applied', 'lens_created', 'text'].includes(block.type));
  const showActionRow = settledCleanly && !fault && !lensConversationTurn && showAnswerActions;
  const answerMessage = answerMessageOf(turn);
  const receiptProps = {
    blocks: turn.blocks,
    provenance,
    tablesInline: !chartLed,
    debugUsage: debug ? turn.turnUsage : undefined,
  };
  const workInline = showActionRow && turn.blocks.length > 0;
  const work = useWorkSummary({
    steps: turn.timeline,
    status,
    debug,
    messageId: turn.id,
    sessionId: turn.sessionId,
    turnUsage: turn.turnUsage,
    sessionUsage: turn.usage,
    elapsedMs,
    inline: workInline,
    method:
      workInline && hasAnswerMethod(receiptProps) ? (
        <AnswerMethod {...receiptProps} onSavedLens={onSavedLens} />
      ) : undefined,
  });
  const [actionRowHost, setActionRowHost] = useState<HTMLDivElement | null>(null);
  const answer = (
    <LeDataMessageBlocks
      messageId={turn.id}
      blocks={chartLed ? withoutTableOnlyProse(turn.blocks) : turn.blocks}
      sourceQuestion={sourceQuestion}
      isStreaming={live}
      textStreaming={live}
      frameless={settledCleanly}
      hideEvidence
      prominentLensSave={prominentLensSave && !chartLed}
      onBuildDashboard={prominentLensSave && settledCleanly ? onBuildDashboard : undefined}
      dashboardDestinations={dashboardDestinations}
      onSavedLens={onSavedLens}
      latestLensScopeBlockId={lens.latestLensScopeBlockId}
      latestLensCreatedBlockId={lens.latestLensCreatedBlockId}
      lensTurnSending={lens.lensTurnSending}
      lensTurnAnswerText={replyText}
      lensTurnQuestionAnswer={replyQuestionAnswer}
      onSendLensTurn={lens.onSendLensTurn}
      onLensCreated={lens.onLensCreated}
      lensSaveTarget={lensSaveTarget}
      allowGlobalLensDrafts={allowGlobalLensDrafts}
      forcedReplacementCard={forcedReplacementCard}
      cardApply={cardApply}
      actionRow={
        showActionRow
          ? (doors) =>
              actionRowHost
                ? createPortal(
                    <>
                      <AnswerActions
                        message={answerMessage}
                        sessionId={turn.sessionId}
                        doors={doors}
                        work={work.toggle}
                        lensUndo={<AnswerLensUndo blocks={turn.blocks} />}
                        speech={speech}
                      />
                      {work.details}
                    </>,
                    actionRowHost,
                  )
                : null
          : undefined
      }
    />
  );

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      className="ui-stream-turn"
      data-testid="ui-stream-assistant-turn"
      data-status={status}
      sx={{ minWidth: 0 }}
    >
      <RichardMonogram
        inverted={theme.palette.mode === 'dark'}
        titleAccess="askRichard"
        sx={{ display: 'flex', fontSize: RICHARD_MARK_SIZES.chatMessage, mt: 0.25, flex: 'none' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SteerNotes steers={turn.steers} pending={live ? pendingSteers : []} />
        {live && !awaitingLensAnswer ? (
          <LiveWork
            key={turn.id}
            rows={activityRows}
            statusText={statusText}
            active={live && !awaitingLensAnswer}
          />
        ) : null}
        <ProposalCollapse.Provider value={setDraftCollapsed}>
          <Box ref={answerArea.ref} sx={{ minHeight: answerArea.minHeight }}>
            {live && !hasAnswerBody ? <AnswerSkeleton compact={compactSkeleton} /> : null}
            {turn.blocks.length > 0 ? (
              <AnswerCard
                framed={framed}
                variant="bubble"
                receipt={
                  framed && settledCleanly && hasAnswerReceipt(receiptProps, workInline) ? (
                    <AnswerReceipt {...receiptProps} methodElsewhere={workInline} onSavedLens={onSavedLens} />
                  ) : undefined
                }
              >
                {answer}
              </AnswerCard>
            ) : null}
          </Box>
        </ProposalCollapse.Provider>
        {settledCleanly && selfFramed && hasAnswerReceipt(receiptProps, workInline) ? (
          <Box sx={{ mt: 1 }}>
            <AnswerReceipt {...receiptProps} methodElsewhere={workInline} onSavedLens={onSavedLens} />
          </Box>
        ) : null}
        {showActionRow ? <Box ref={setActionRowHost} /> : null}
        {fault && fault.kind !== 'stopped' ? <TurnFaultAlert fault={fault} /> : null}
        {live || workInline || !work.toggle ? null : (
          <Box sx={{ mt: 1 }}>
            {work.toggle}
            {work.details}
          </Box>
        )}
      </Box>
    </Stack>
  );
});
